import moment from 'moment';
import { getIn, replaceInvalidCharacters, setIn } from '../helpers/helpers';
import { FieldValue, Form, PublicForm } from '../types/form-types';
import { getGuid } from './../helpers/utility';
import { FieldValueUpdate, FormEditorState, FormulaValueUpdate, SavedPublicForm, ValidationResultsUpdate } from './formeditor-types';
import { getValueByFieldName, getValueKeyPathByFieldId } from './selectors';
import { getMissingRequiredFields, getMissingRequiredFieldsViewPositions } from './requiredFields';
import { checkIfCalculationValueIsTrue } from './helpers';
import { getInvalidFieldValues } from './components/FieldValidations/helpers/validatedFieldsHelpers';
import { getViewPositionsForFieldIds } from './components/FieldValidations/selectors';

const initialState = {} as FormEditorState;

export const initForm = (state: FormEditorState, payload): FormEditorState => {
  // if there is a session set when initializing the form, we've
  // come from the PublicForm component, otherwise we've loaded a form
  // from the forms list
  const hasSessionId = !!payload.sessionId;
  // todo - clear out send a form state
  return hasSessionId ? { ...state, ...payload, hipaaForm: null, sendAForm: null } : { ...initialState, ...payload, sendAForm: null };
};

export const updateName = (state: FormEditorState, payload): FormEditorState => {
  const { manualNameOverride, autoGenerated, dateTimeFormat } = payload;
  let { name } = payload;

  if (!autoGenerated || !state?.form?.manualNameOverride) {
    if (!name && !autoGenerated) {
      state.error = {
        title: 'Name Required',
        message: 'A name is required. Please enter a name.'
      };
      return state;
    } else if (!name) {
      // we don't want to ever have a form with an empty name. We'll default to Template Name Date
      const defaultName = state.template.name;
      name = `${defaultName} ${moment().format(dateTimeFormat)}`;
    }
    state.form.name = name.substring(0, 200);
    state.isUnsaved = true;
  }

  if (manualNameOverride) {
    state.form.manualNameOverride = true;
    state.isUnsaved = true;
  }
  return state;
};

export const createForm = (state: FormEditorState, userId): FormEditorState => {
  const form = {
    id: state.form?.id || getGuid(),
    templateId: state.template?.id,
    templateVersionId: state.template?.versionId,
    userId,
    name: state.template?.name,
    values: state.form?.values || [],
    tags: []
  } as Form;

  state.form = form;
  return state;
};

export const createFieldValue = (state: FormEditorState, fieldId: string, fieldName: string): FormEditorState => {
  let values = state.form?.values;
  if (!values) {
    values = [];
  }
  const value: FieldValue = {
    fieldId,
    fieldName
  };

  values.push(value);

  state.form.values = values;
  return state;
};

export const updateFieldValues = (
  state: FormEditorState,
  fieldValueUpdateList: FieldValueUpdate[],
  userFullName: string,
  userEmail: string,
  dateTimeFormat: string
): FormEditorState => {
  if (fieldValueUpdateList) {
    fieldValueUpdateList.forEach((fieldValueUpdate) => {
      const { value, fieldId, fieldName, rowNumber, columnNumber, coordinates, dateTimeValue, timeValue } = fieldValueUpdate;
      let valueKeyPath = getValueKeyPathByFieldId(state.form, fieldId);

      // this is hacky since dispatching a form init multiple times without a form id (public share/hipaa) can result
      // in the form not being loaded when this function is called
      if (valueKeyPath === undefined) {
        // ignore
        return;
      }

      if (!valueKeyPath) {
        state = createFieldValue(state, fieldId, fieldName);
        valueKeyPath = getValueKeyPathByFieldId(state.form, fieldId);
      }

      const fieldValue = getIn<TypeMePlease>(state, ['form', ...valueKeyPath]);
      if (rowNumber !== null && rowNumber !== undefined && columnNumber !== null && columnNumber !== undefined) {
        let rowValues: TypeMePlease[] = fieldValue.rows;
        if (!rowValues) {
          rowValues = [];
        }
        let rowValueIndex = rowValues.findIndex((x) => x.rowNumber === rowNumber);
        if (rowValueIndex === -1) {
          rowValueIndex = rowValues.length;
          rowValues[rowValueIndex] = {
            rowNumber,
            columns: []
          };
        }

        if (!rowValues[rowValueIndex].columns) {
          rowValues = rowValues[rowValueIndex].columns = [];
        }

        const columnValues = rowValues[rowValueIndex].columns;
        let cellValueIndex = columnValues.findIndex((x) => x.columnNumber === columnNumber);
        if (cellValueIndex === -1) {
          cellValueIndex = columnValues.length;
          rowValues[rowValueIndex].columns[cellValueIndex] = { columnNumber };
        }

        const cellValue = rowValues[rowValueIndex]?.columns[cellValueIndex] ?? {};
        const rowValuesUpdateCount = (cellValue.updatecount || 1) + 1;
        cellValue.value = value;
        cellValue.dateTimeValue = dateTimeValue;
        cellValue.timeValue = timeValue;
        cellValue.operation = 2;
        cellValue.updatecount = rowValuesUpdateCount;

        if (coordinates) {
          cellValue.longitude = coordinates.longitude;
          cellValue.latitude = coordinates.latitude;
        }

        fieldValue.rows = rowValues;
      } else {
        const fieldValueUpdateCount = (fieldValue.updatecount || 1) + 1;
        fieldValue.value = value;
        fieldValue.dateTimeValue = dateTimeValue;
        fieldValue.timeValue = timeValue;
        fieldValue.updatecount = fieldValueUpdateCount;

        if (coordinates) {
          fieldValue.longitude = coordinates.longitude;
          fieldValue.latitude = coordinates.latitude;
        }
      }

      fieldValue.operation = 2;
      fieldValue.fieldName = fieldName;
      setIn(state, ['form', ...valueKeyPath], fieldValue);
    });

    updateFormName(state, userFullName, userEmail, dateTimeFormat);
    updateMissingRequiredFields(state);
  }
  return state;
};

export const updateFormulaValues = (state: FormEditorState, list?: FormulaValueUpdate[]): FormEditorState => {
  // formulaValues should be undefined when list is falsy/empty
  if (list?.length > 0) {
    if (!state.formulaValues) {
      // todo - fix types
      state.formulaValues = {} as ExplicitAny;
    }

    list.forEach((fv) => {
      const { id, key, value } = fv;
      state.formulaValues[`${id}_${key}`] = value;
    });
  }

  return state;
};

export const updateValidationResults = (state: FormEditorState, updateList?: ValidationResultsUpdate[]): FormEditorState => {
  if (!updateList?.length) return state;

  if (!state.invalidFieldValues) {
    state.invalidFieldValues = [];
  }

  updateList.forEach(({ fieldId, fieldName, result, errorSettings }) => {
    if (checkIfCalculationValueIsTrue(result)) {
      // Remove if it exists
      state.invalidFieldValues = state.invalidFieldValues.filter((invalidField) => invalidField.fieldId !== fieldId);
    } else {
      // Add only if it doesn’t already exist and the field has no value
      if (!state.invalidFieldValues.some((invalidField) => invalidField.fieldId === fieldId)) {
        const formValue = state.form?.values?.find((x) => x.fieldId === fieldId);
        if (formValue?.value !== '' && formValue?.value !== null && formValue?.value !== undefined) {
          state.invalidFieldValues.push({ fieldId, fieldName, errorSettings });
        }
      }
    }
  });

  return state;
};

function updateFormName(state: FormEditorState, userFullName: string, userEmail: string, dateTimeFormat: string) {
  const defaultFormName = state.template?.defaultFormName;
  if (defaultFormName && defaultFormName.substring(0, 1) !== '=') {
    // this is an old style default form name
    const replacements = defaultFormName
      .split('[') //an array where each item starts with a field name
      .map((x) => x.split(']')) // an array of arrays where the first item is a field name
      .filter((x) => x.length > 1) //remove arrays that don't start with a field name
      .map((x) => x[0]); //get the associated value

    let newFormName = defaultFormName;
    for (const replacement of replacements) {
      let value = null;
      switch (replacement.toLowerCase()) {
        case 'formname':
        case 'templatename':
          value = state.template.name;
          break;

        case 'employeename':
          value = userFullName;
          break;

        case 'emailaddress':
        case 'username':
          value = userEmail;
          break;

        default:
          value = getValueByFieldName(state.template, state.form, replacement);
          break;
      }

      newFormName = newFormName.replace(`[${replacement}]`, value || ''); //replace the field names in the default form name
      newFormName = replaceInvalidCharacters(newFormName);
    }

    if (!newFormName) {
      // we don't want to ever have a form with an empty name. We'll default to Template Name Date
      const defaultName = state.template.name;
      newFormName = `${defaultName} ${moment().format(dateTimeFormat)}`;
    }

    if (state.form?.name !== newFormName && !state.form?.manualNameOverride) {
      state.form.name = newFormName;
      state.isUnsaved = true;
    }
  }
  return state;
}

function updateMissingRequiredFields(state: FormEditorState) {
  if (state.missingRequiredFieldIds?.length > 0) {
    const missingRequiredFields = getMissingRequiredFields(state);

    if (state.highlight != null && !missingRequiredFields?.some((x) => x.id === state.highlight?.fieldId)) {
      const previousMissingRequiredFieldPositions = getMissingRequiredFieldsViewPositions(state);
      const previousHighlightIndex = previousMissingRequiredFieldPositions.findIndex((x) => x.field.id === state.highlight?.fieldId);
      state.highlight = null;

      for (let i = previousHighlightIndex + 1; i < previousMissingRequiredFieldPositions.length; i++) {
        if (missingRequiredFields.some((x) => x.id === previousMissingRequiredFieldPositions[i].field.id)) {
          state.highlight = {
            fieldId: previousMissingRequiredFieldPositions[i].field.id,
            fieldPositionId: previousMissingRequiredFieldPositions[i].fieldPosition?.id,
            dataLayoutPositionId: previousMissingRequiredFieldPositions[i].dataLayoutPosition?.id
          };
          break;
        }
      }
    }

    state.missingRequiredFieldIds = missingRequiredFields.map((x) => x.id);
  }

  return state;
}

export const closeForm = (state: FormEditorState): FormEditorState => {
  state.form = null;
  state.template = null;
  state.templatedLoaded = false;
  state.formulaValues = null;
  state.sendAForm = null;
  return state;
};

export const completeForm = (state: FormEditorState, payload): FormEditorState => {
  if (payload) {
    const { longitude, latitude } = payload;
    if (longitude && latitude) {
      state.form.longitude = longitude;
      state.form.latitude = latitude;
    }
  }

  state.form.completedDate = moment.utc().format('YYYY-MM-DDTHH:mm:ss');
  return state;
};

export const reopenFormSuccess = (state: FormEditorState, payload): FormEditorState => {
  state.isSaving = payload.isSaving;
  state.isDirty = payload.isDirty;
  state.lastSaveDate = payload.lastSaveDate;
  state.isUnsaved = payload.isUnsaved;
  state.form.completedDate = null;
  state.form.lastUpdatedDate = moment.utc().format('YYYY-MM-DDTHH:mm:ss');
  return state;
};

export const updateTags = (state: FormEditorState, payload): FormEditorState => {
  state.form.tags = payload.tags;
  return state;
};

export const updateFieldHighlight = (state: FormEditorState, payload): FormEditorState => {
  const { fields } = payload;
  const fieldIds = fields.map((x) => x.fieldId);

  // Filter fieldIds with missing required fields
  const missingRequiredFieldIds = getMissingRequiredFields(state)?.map((x) => x.id);
  const filteredMissingRequiredFieldIds = missingRequiredFieldIds?.filter((x) => fieldIds.includes(x));
  const missingRequiredFieldViewPositions = getViewPositionsForFieldIds(state, filteredMissingRequiredFieldIds);

  // Filter fieldIds with invalid field values
  const invalidFieldValues = getInvalidFieldValues(state);
  const invalidFieldValueIds = invalidFieldValues?.map((x) => x.fieldId);
  const filteredInvalidFieldValueIds = invalidFieldValueIds?.filter((x) => fieldIds.includes(x));
  const invalidFieldValuesViewPositions = getViewPositionsForFieldIds(state, filteredInvalidFieldValueIds);

  // Get highlighted field id
  const highlightedFieldId = state.highlight?.fieldId;

  if (highlightedFieldId && filteredMissingRequiredFieldIds?.length > 0) {
    // Highlight does not exist, so set to the first missing required field
    const firstMissingRequiredField = missingRequiredFieldViewPositions[0];

    if (!firstMissingRequiredField) {
      return state;
    }

    state.highlight = {
      fieldId: firstMissingRequiredField.field.id,
      fieldPositionId: firstMissingRequiredField.fieldPosition?.id,
      dataLayoutPositionId: firstMissingRequiredField.dataLayoutPosition?.id
    };

    state.missingRequiredFieldIds = missingRequiredFieldIds;
  } else if (filteredInvalidFieldValueIds?.length > 0) {
    // set to the first invalid field value
    const firstInvalidFieldValue = invalidFieldValuesViewPositions[0];

    if (!firstInvalidFieldValue) {
      return state;
    }

    state.highlight = {
      fieldId: firstInvalidFieldValue.field.id,
      fieldPositionId: firstInvalidFieldValue.fieldPosition?.id,
      dataLayoutPositionId: firstInvalidFieldValue.dataLayoutPosition?.id
    };

    state.invalidFieldValues = invalidFieldValues;
  }

  return state;
};

export const updateTemplateVersion = (state: FormEditorState, payload): FormEditorState => {
  state.form.templateVersionId = payload.templateVersionId;
  return state;
};

export function formSaveSuccess(state: FormEditorState, payload): FormEditorState {
  if (state.form?.values?.length > 0) {
    for (let i = 0; i < state.form?.values.length; i++) {
      state.form.values[i].operation = 0;
    }

    state.form.lastUpdatedDate = payload.lastUpdatedDate;
  }
  state.isSaving = payload.isSaving;
  state.isDirty = payload.isDirty;
  state.lastSaveDate = payload.lastSaveDate;
  state.isUnsaved = payload.isUnsaved;
  state.publicShareSubmitted = payload.publicShareSubmitted;
  state.pdfPath = payload.pdfPath;

  return state;
}

export const updateSavedPublicFormState = (state: FormEditorState, savedPublicForm: SavedPublicForm): FormEditorState => {
  if (!state.savedPublicForm?.saveId) {
    state.savedPublicForm = savedPublicForm;
    return state;
  }

  state.savedPublicForm.lastSaveDate = savedPublicForm.lastSaveDate;
  state.isUnsaved = false;
  return state;
};

export const updateSavedPublicFormBlobState = (state: FormEditorState, savedPublicFormBlob: PublicForm): FormEditorState => {
  state.savedPublicForm.blobData = savedPublicFormBlob;
  return state;
};
